import {useState} from 'react'
import {ICreateAppData, StepProps} from '../IAppModels'
import {useAuth} from '../../../../../app/modules/auth'
import {toast} from 'react-toastify'
import {Spinner} from 'react-bootstrap'
import CustomCalendar from '../../../../../_metronic/layout/calendar'

type Props = {
  data: ICreateAppData
  updateData: (fieldsToUpdate: Partial<ICreateAppData>) => void
  hasError: boolean
  onFileUpload: (category: string, files: File[]) => void
  fileCategories: {
    resumeMedis: File[]
    billingAkhir: File[]
    hasilPenunjang: File[]
  }
  onDischargeDateChange: (date: string) => void
  claimNumber: string
}

// First, add a new type for the upload response
type UploadResponse = {
  isSuccess: boolean
  message: string
  title: string | null
  data: Array<{
    id: number
    fileName: string
    label: string
    area: string
  }>
}

const Step1 = ({
  data,
  updateData,
  onFileUpload,
  fileCategories,
  onDischargeDateChange,
  claimNumber,
}: Props) => {
  const {auth, currentUser} = useAuth()
  const [selectedFiles, setSelectedFiles] = useState<
    Array<{
      file: File
      category: string
      id: number
    }>
  >([])
  const [outdatedDate, setOutdatedDate] = useState<string>('')

  // Add upload progress tracking
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({
    resumeMedis: 0,
    billingAkhir: 0,
    hasilPenunjang: 0,
  })
  const [isUploading, setIsUploading] = useState<Record<string, boolean>>({
    resumeMedis: false,
    billingAkhir: false,
    hasilPenunjang: false,
  })
  const [successMessage, setSuccessMessage] = useState<Record<string, string>>({
    resumeMedis: '',
    billingAkhir: '',
    hasilPenunjang: '',
  })

  // Helper function to format category name
  const formatTypeDocument = (category: string): string => {
    switch (category) {
      case 'resumeMedis':
        return 'Resume Medis'
      case 'billingAkhir':
        return 'Billing Akhir'
      case 'hasilPenunjang':
        return 'Hasil Penunjang'
      default:
        return category
    }
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>, category: string) => {
    const files = e.target.files
    if (!files) return

    const newFiles: File[] = Array.from(files)

    try {
      // Set uploading state for this category
      setIsUploading((prev) => ({...prev, [category]: true}))
      setUploadProgress((prev) => ({...prev, [category]: 0}))
      setSuccessMessage((prev) => ({...prev, [category]: ''}))

      for (const file of newFiles) {
        const formData = new FormData()
        formData.append('files', file)

        const formattedTypeDocument = formatTypeDocument(category)
        const uploadUrl = `${
          process.env.REACT_APP_API_CLAIM_URL
        }/api/Claims/UploadFiles?claimNumber=${claimNumber}&typeDocument=${encodeURIComponent(
          formattedTypeDocument
        )}&user=${currentUser?.email || 'SYSTEM'}&bucket=Discharge`

        // Simulate progress for fetch (since fetch doesn't support progress natively)
        let simulatedProgress = 0
        const progressInterval = setInterval(() => {
          simulatedProgress += Math.random() * 15
          if (simulatedProgress > 95) {
            simulatedProgress = 95 // Cap at 95% until complete
            clearInterval(progressInterval)
          }
          setUploadProgress((prev) => ({...prev, [category]: Math.floor(simulatedProgress)}))
        }, 300)

        const uploadResponse = await fetch(uploadUrl, {
          method: 'POST',
          body: formData,
          headers: {
            Authorization: `Bearer ${auth?.token || currentUser?.token}`,
          },
        })

        clearInterval(progressInterval)
        setUploadProgress((prev) => ({...prev, [category]: 100}))

        if (!uploadResponse.ok) {
          throw new Error(`Failed to upload ${file.name}`)
        }

        const responseData: UploadResponse = await uploadResponse.json()

        if (responseData.isSuccess && responseData.data.length > 0) {
          const fileId = responseData.data[0].id

          // Only add to state if upload was successful
          const newFileObject = {
            file,
            category,
            id: fileId,
          }

          setSelectedFiles((prevFiles) => [...prevFiles, newFileObject])
          onFileUpload(category, [file])

          updateData({
            appFiles: {
              ...data.appFiles,
              files: [...selectedFiles, newFileObject].map((f) => f.file),
            },
          })

          setSuccessMessage((prev) => ({...prev, [category]: `Successfully uploaded ${file.name}`}))
          toast.success(`Successfully uploaded ${file.name}`)

          // Auto-clear success message after 3 seconds
          setTimeout(() => {
            setSuccessMessage((prev) => ({...prev, [category]: ''}))
          }, 3000)
        }
      }
    } catch (error) {
      console.error('Upload error:', error)
      toast.error(`Failed to upload: ${(error as Error).message}`)
    } finally {
      // Reset uploading state for this category
      setIsUploading((prev) => ({...prev, [category]: false}))
    }
  }

  const removeFile = async (fileId: number, index: number, category: string) => {
    try {
      const deleteUrl = `${process.env.REACT_APP_API_CLAIM_URL}/api/Claims/Provider/DeleteFile?id=${fileId}&claimNumber=${claimNumber}`

      const deleteResponse = await fetch(deleteUrl, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${auth?.token || currentUser?.token}`,
        },
      })

      if (!deleteResponse.ok) {
        throw new Error('Failed to delete file')
      }

      // Remove from selectedFiles state
      const updatedFiles = selectedFiles.filter((_, i) => i !== index)
      setSelectedFiles(updatedFiles)

      // Get the file to be removed
      const fileToRemove = selectedFiles[index]

      // Update the form data
      updateData({
        appFiles: {
          ...data.appFiles,
          files: updatedFiles.map((f) => f.file),
        },
      })

      // Update fileCategories through onFileUpload
      // We pass an empty array to effectively remove the file from the category
      const remainingFiles = selectedFiles
        .filter((f) => f.category === category && f.id !== fileId)
        .map((f) => f.file)
      onFileUpload(category, remainingFiles)

      toast.success('File deleted successfully')
    } catch (error) {
      console.error('Delete error:', error)
      toast.error(`Failed to delete: ${(error as Error).message}`)
    }
  }

  const handleOutdatedDateChange = (index: number, date: string) => {
    setSelectedFiles((prevFiles) =>
      prevFiles.map((file, i) => (i === index ? {...file, outdatedDate: date} : file))
    )
  }

  const handleDischargeDate = (date: string) => {
    setOutdatedDate(date) // Keep the date format for the input display
    onDischargeDateChange(date) // Pass the date string to parent
  }

  return (
    <>
      <div className='pb-5' data-kt-stepper-element='content'>
        <div className='w-100'>
          <div className='fv-row mb-5'>
            <CustomCalendar
              label='Discharge Date'
              value={outdatedDate}
              onChange={handleDischargeDate}
              placeholder='Select discharge date'
              className='mb-3'
            />
          </div>

          <div className='separator separator-dashed my-4'></div>

          {/* Resume Medis Section */}
          <div className='fv-row mb-5'>
            <label className='fs-6 fw-semibold mb-2'>Resume Medis</label>
            <div className='border-dashed p-3'>
              <FileUploadArea
                onFileChange={(e) => handleFileChange(e, 'resumeMedis')}
                category='resumeMedis'
                files={selectedFiles.filter((f) => f.category === 'resumeMedis')}
                onRemove={(fileId, index) => removeFile(fileId, index, 'resumeMedis')}
                isUploading={isUploading.resumeMedis}
                uploadProgress={uploadProgress.resumeMedis}
                successMessage={successMessage.resumeMedis}
              />
            </div>
          </div>

          {/* Billing Akhir Section */}
          <div className='fv-row mb-5'>
            <label className='fs-6 fw-semibold mb-2'>Billing Akhir</label>
            <div className='border-dashed p-3'>
              <FileUploadArea
                onFileChange={(e) => handleFileChange(e, 'billingAkhir')}
                category='billingAkhir'
                files={selectedFiles.filter((f) => f.category === 'billingAkhir')}
                onRemove={(fileId, index) => removeFile(fileId, index, 'billingAkhir')}
                isUploading={isUploading.billingAkhir}
                uploadProgress={uploadProgress.billingAkhir}
                successMessage={successMessage.billingAkhir}
              />
            </div>
          </div>

          {/* Hasil Penunjang Section */}
          <div className='fv-row mb-5'>
            <label className='fs-6 fw-semibold mb-2'>Hasil Penunjang</label>
            <div className='border-dashed p-3'>
              <FileUploadArea
                onFileChange={(e) => handleFileChange(e, 'hasilPenunjang')}
                category='hasilPenunjang'
                files={selectedFiles.filter((f) => f.category === 'hasilPenunjang')}
                onRemove={(fileId, index) => removeFile(fileId, index, 'hasilPenunjang')}
                isUploading={isUploading.hasilPenunjang}
                uploadProgress={uploadProgress.hasilPenunjang}
                successMessage={successMessage.hasilPenunjang}
              />
            </div>
          </div>
        </div>
      </div>

      <style>
        {`
          .selected-files > div::-webkit-scrollbar {
            width: 6px;
          }

          .selected-files > div::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 3px;
          }

          .selected-files > div::-webkit-scrollbar-thumb {
            background: #dee2e6;
            border-radius: 3px;
          }

          .selected-files > div::-webkit-scrollbar-thumb:hover {
            background: #c1c9d0;
          }
        `}
      </style>
    </>
  )
}

// Update the FileUploadArea component to include progress bar and more compact design
const FileUploadArea = ({
  onFileChange,
  category,
  files,
  onRemove,
  isUploading,
  uploadProgress,
  successMessage,
}: {
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  category: string
  files: Array<{file: File; id: number}>
  onRemove: (fileId: number, index: number) => void
  isUploading: boolean
  uploadProgress: number
  successMessage: string
}) => (
  <div style={{backgroundColor: '#f8f9fa', borderRadius: '8px'}}>
    {/* Upload Area - More compact design */}
    <div
      className='d-flex align-items-center justify-content-between p-3'
      style={{
        border: '2px dashed #dee2e6',
        borderRadius: '8px',
        cursor: 'pointer',
        position: 'relative',
        backgroundColor: '#f8f9fa',
        minHeight: '60px', // Reduced height
      }}
    >
      <div className='d-flex align-items-center'>
        <i className='fas fa-upload fs-2 me-3 text-primary'></i>
        <div>
          <span className='text-dark fw-semibold d-block fs-7'>
            Upload{' '}
            {category === 'resumeMedis'
              ? 'Resume Medis'
              : category === 'billingAkhir'
              ? 'Billing Akhir'
              : 'Hasil Penunjang'}{' '}
            files
          </span>
          <span className='text-muted fs-8'>.pdf, .jpg, .png, .jpeg files (max 10MB)</span>
        </div>
      </div>
      <input
        type='file'
        className='form-control form-control-lg form-control-solid'
        name={`upload${category}`}
        onChange={onFileChange}
        accept='.pdf,.jpg,.jpeg,.png'
        multiple
        style={{
          opacity: 0,
          position: 'absolute',
          width: '100%',
          height: '100%',
          cursor: 'pointer',
          zIndex: 2,
        }}
      />
      <button className='btn btn-sm btn-light-primary btn-icon' style={{zIndex: 1}}>
        <i className='fas fa-plus'></i>
      </button>
    </div>

    {/* Upload Progress Bar */}
    {isUploading && (
      <div
        className='mt-3'
        style={{
          background: 'white',
          padding: '12px',
          borderRadius: '8px',
          boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
          border: '1px solid #e9ecef',
        }}
      >
        <div
          style={{
            marginBottom: '8px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              color: '#059BB6',
              fontWeight: 500,
              fontSize: '13px',
            }}
          >
            <Spinner
              animation='border'
              size='sm'
              style={{
                marginRight: '8px',
                color: '#059BB6',
                width: '14px',
                height: '14px',
              }}
            />
            Uploading...
          </span>
          <span
            style={{
              background: '#059BB6',
              color: 'white',
              borderRadius: '20px',
              padding: '1px 8px',
              fontSize: '12px',
              fontWeight: 500,
            }}
          >
            {uploadProgress}%
          </span>
        </div>
        <div
          style={{
            background: '#f0f0f0',
            borderRadius: '4px',
            height: '6px',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: `${uploadProgress}%`,
              height: '6px',
              background: 'linear-gradient(90deg, #059BB6 0%, #05b6a6 100%)',
              borderRadius: '4px',
              transition: 'width 0.3s ease',
            }}
          />
        </div>
      </div>
    )}

    {/* Success Message */}
    {successMessage && (
      <div
        style={{
          marginTop: '10px',
          padding: '8px 12px',
          background: '#d4edda',
          color: '#155724',
          borderRadius: '6px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 500,
          fontSize: '13px',
          boxShadow: '0 2px 4px rgba(21, 87, 36, 0.1)',
        }}
      >
        <i className='fas fa-check-circle me-2'></i>
        {successMessage}
      </div>
    )}

    {/* Compact File List */}
    {files.length > 0 && (
      <div className='mt-3 selected-files'>
        <div
          style={{
            maxHeight: '150px', // Reduced height
            overflowY: 'auto',
            borderRadius: '8px',
            border: '1px solid #eee',
            padding: '8px', // Reduced padding
          }}
        >
          {files.map((fileObj, index) => (
            <div
              key={index}
              className='d-flex align-items-center justify-content-between p-2 bg-light-primary rounded mb-2'
              style={{borderBottom: index !== files.length - 1 ? '1px solid #e9ecef' : 'none'}}
            >
              <div className='d-flex align-items-center'>
                <i className='fas fa-file-alt text-primary me-2 fs-4'></i>
                <div>
                  <span
                    className='fw-bold d-block fs-7'
                    style={{
                      maxWidth: '150px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {fileObj.file.name}
                  </span>
                  <span className='text-muted fs-8'>
                    {(fileObj.file.size / 1024 / 1024).toFixed(2)} MB
                  </span>
                </div>
              </div>
              <button
                type='button'
                className='btn btn-icon btn-sm btn-light-danger'
                onClick={() => onRemove(fileObj.id, index)}
                style={{
                  width: '24px',
                  height: '24px',
                  padding: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <i className='fas fa-times fs-7'></i>
              </button>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
)

export {Step1}
